import {
  GroupedOutcomeObservationPreviewStockUnit,
  MovementArticle,
  MovementArticleStockUnitSelection,
} from "types";

type DraftMovementArticleLineData = {
  draft_not_available?: number | null;
  draft_observation_available?: string | null;
  draft_observation_not_available?: string | null;
  draft_stock_unit_selection?: MovementArticleStockUnitSelection | null;
  draft_observation_override?: boolean;
  draft_observation_preview_units?:
    | GroupedOutcomeObservationPreviewStockUnit[]
    | null;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value != null && typeof value === "object" && !Array.isArray(value);

const toNullableString = (value: unknown) => {
  if (value === null) {
    return null;
  }

  return typeof value === "string" ? value : undefined;
};

const toNumber = (value: unknown) =>
  typeof value === "number" && Number.isFinite(value) ? value : undefined;

const toBoolean = (value: unknown) =>
  typeof value === "boolean" ? value : undefined;

const toStockUnitSelection = (
  value: unknown,
): MovementArticleStockUnitSelection | undefined => {
  if (!isRecord(value) || !Array.isArray(value.stock_unit_ids)) {
    return undefined;
  }

  const stockUnitIds = value.stock_unit_ids
    .map(Number)
    .filter((stockUnitId): stockUnitId is number => Number.isInteger(stockUnitId));

  const observationOverrides = Array.isArray(value.observation_overrides)
    ? value.observation_overrides
        .filter(isRecord)
        .map((override) => ({
          stock_unit_id: Number(override.stock_unit_id),
          observation:
            override.observation == null || typeof override.observation === "string"
              ? (override.observation as string | null | undefined) ?? null
              : null,
        }))
        .filter((override) => Number.isInteger(override.stock_unit_id))
    : [];

  return {
    stock_unit_ids: stockUnitIds,
    observation_overrides: observationOverrides,
  };
};

const toObservationPreviewUnits = (
  value: unknown,
): GroupedOutcomeObservationPreviewStockUnit[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }

  return value
    .filter(isRecord)
    .map((previewUnit) => ({
      ...(previewUnit as unknown as GroupedOutcomeObservationPreviewStockUnit),
    }));
};

export const hydrateDraftMovementArticle = (
  article: MovementArticle,
): MovementArticle => {
  if (!isRecord(article.line_data)) {
    return article;
  }

  const lineData = article.line_data as DraftMovementArticleLineData;
  const draftNotAvailable = toNumber(lineData.draft_not_available);
  const observationAvailable = toNullableString(
    lineData.draft_observation_available,
  );
  const observationNotAvailable = toNullableString(
    lineData.draft_observation_not_available,
  );
  const stockUnitSelection = toStockUnitSelection(
    lineData.draft_stock_unit_selection,
  );
  const observationOverride = toBoolean(lineData.draft_observation_override);
  const observationPreviewUnits = toObservationPreviewUnits(
    lineData.draft_observation_preview_units,
  );

  return {
    ...article,
    ...(stockUnitSelection ? { stock_unit_selection: stockUnitSelection } : {}),
    ...(observationOverride !== undefined
      ? { observation_override: observationOverride }
      : {}),
    ...(observationPreviewUnits
      ? { observation_preview_units: observationPreviewUnits }
      : {}),
    article_locations: {
      ...article.article_locations,
      ...(draftNotAvailable !== undefined
        ? { not_available: draftNotAvailable }
        : {}),
      ...(observationAvailable !== undefined
        ? {
            observation_available: observationAvailable,
          }
        : {}),
      ...(observationNotAvailable !== undefined
        ? { observation_not_available: observationNotAvailable }
        : {}),
    },
  };
};

export const hydrateDraftMovementArticles = (articles: MovementArticle[] = []) =>
  articles.map(hydrateDraftMovementArticle);


